import type {
  PuzzleRole,
  PlayerStatus,
  PlayerAssignment,
  ValidationError,
  GameLevel,
  NightEvent,
  DayEvent,
  SheriffClaim,
} from '../types/game';

export function nextRole(current: PuzzleRole): PuzzleRole {
  const cycle: PuzzleRole[] = ['grey', 'red', 'black', 'sheriff'];
  return cycle[(cycle.indexOf(current) + 1) % cycle.length];
}

export function deriveStatuses(
  nights: NightEvent[],
  days: DayEvent[],
): Record<number, PlayerStatus> {
  const statuses: Record<number, PlayerStatus> = {};
  for (const night of nights) {
    statuses[night.killedPlayer] = 'killed';
  }
  for (const day of days) {
    if (day.evictedPlayer !== null) {
      statuses[day.evictedPlayer] = 'evicted';
    }
  }
  return statuses;
}

export function isDefinitive(role: PuzzleRole): boolean {
  return role === 'red' || role === 'black' || role === 'sheriff';
}

export function countRoles(assignments: PlayerAssignment[]): {
  black: number;
  red: number;
  sheriff: number;
  grey: number; // includes maybe-black, maybe-red
} {
  return assignments.reduce(
    (acc, a) => {
      if (a.role === 'black') acc.black++;
      else if (a.role === 'sheriff') acc.sheriff++;
      else if (a.role === 'red') acc.red++;
      else acc.grey++; // grey | maybe-black | maybe-red all count as unresolved
      return acc;
    },
    { black: 0, red: 0, sheriff: 0, grey: 0 },
  );
}

export function checkBlackCount(assignments: PlayerAssignment[]): ValidationError | null {
  const assignedBlacks = assignments.filter((a) => a.role === 'black');
  if (assignedBlacks.length > 3) {
    return {
      code: 'TOO_MANY_BLACK',
      message: 'Слишком много мафии — за столом может быть не более 3 чёрных',
      affectedSeats: assignedBlacks.map((a) => a.seat),
    };
  }
  return null;
}

export function checkSheriffCount(assignments: PlayerAssignment[]): ValidationError | null {
  const assignedSheriffs = assignments.filter((a) => a.role === 'sheriff');
  if (assignedSheriffs.length > 1) {
    return {
      code: 'TOO_MANY_SHERIFF',
      message: 'Только один настоящий Шериф',
      affectedSeats: assignedSheriffs.map((a) => a.seat),
    };
  }
  return null;
}

export function checkNightKillColors(
  assignments: PlayerAssignment[],
  nights: NightEvent[],
): ValidationError | null {
  // Night kills are red-team players (civilian OR sheriff) — never mafia
  const badSeats: number[] = [];
  for (const night of nights) {
    const assignment = assignments.find((a) => a.seat === night.killedPlayer);
    if (assignment && assignment.role === 'black') {
      badSeats.push(night.killedPlayer);
    }
  }
  if (badSeats.length > 0) {
    return {
      code: 'KILLED_IS_NOT_RED',
      message: 'Убитые ночью — всегда красная команда, но не мафия',
      affectedSeats: badSeats,
    };
  }
  return null;
}

export function checkSheriffConsistency(
  assignments: PlayerAssignment[],
  claims: SheriffClaim[],
): ValidationError | null {
  const realSheriff = assignments.find((a) => a.role === 'sheriff');
  if (!realSheriff) return null;

  const sheriffSeat = realSheriff.seat;
  const sheriffClaims = claims.filter((c) => c.claimant === sheriffSeat);

  const badSeats: number[] = [];
  const futureChecks: number[] = [];

  for (const claim of sheriffClaims) {
    for (const check of claim.checks) {
      // Порядок: День N → Ночь N.
      // На Дне N шериф может назвать только проверки из Ночей 1..N-1.
      // (Ночь N ещё не наступила в момент вскрытия на Дне N)
      if (check.night >= claim.day) {
        futureChecks.push(check.checkedPlayer);
        continue;
      }
      const target = assignments.find((a) => a.seat === check.checkedPlayer);
      if (!target) continue;
      const isBlack = target.role === 'black';
      const claimedBlack = check.result === 'black';
      if (isBlack !== claimedBlack) {
        badSeats.push(check.checkedPlayer);
      }
    }
  }

  if (futureChecks.length > 0) {
    return {
      code: 'SHERIFF_FUTURE_CHECK',
      message: 'Шериф не может назвать результат проверки из ночи, которая ещё не наступила',
      affectedSeats: [...new Set([sheriffSeat, ...futureChecks])],
    };
  }
  if (badSeats.length > 0) {
    return {
      code: 'SHERIFF_INCONSISTENT',
      message: 'Настоящий Шериф не лжёт: его проверки должны совпадать с назначенными ролями',
      affectedSeats: [...new Set([sheriffSeat, ...badSeats])],
    };
  }
  return null;
}

export function checkParity(
  assignments: PlayerAssignment[],
  days: DayEvent[],
): ValidationError | null {
  // Check that parity (blacks === reds alive) was not reached before the last recorded day
  // If there are N days, then before day N the game must not have ended
  if (days.length <= 1) return null;

  const killedSeats = new Set<number>();
  const evictedSeats = new Set<number>();

  // Check parity after each night + day pair (except the last day)
  for (let i = 0; i < days.length - 1; i++) {
    const day = days[i];
    if (day.evictedPlayer !== null) evictedSeats.add(day.evictedPlayer);

    const aliveAssignments = assignments.filter(
      (a) => !killedSeats.has(a.seat) && !evictedSeats.has(a.seat),
    );
    const aliveBlacks = aliveAssignments.filter((a) => a.role === 'black').length;
    const aliveReds = aliveAssignments.filter(
      (a) => a.role === 'red' || a.role === 'sheriff',
    ).length;

    if (aliveBlacks > 0 && aliveReds > 0 && aliveBlacks >= aliveReds) {
      return {
        code: 'EARLY_PARITY',
        message: 'Паритет: при таком раскладе мафия победила бы раньше',
        affectedSeats: aliveAssignments
          .filter((a) => a.role === 'black')
          .map((a) => a.seat),
      };
    }
  }
  return null;
}

export function validateAll(assignments: PlayerAssignment[], level: GameLevel): ValidationError[] {
  const errors: ValidationError[] = [];

  const e1 = checkBlackCount(assignments);
  if (e1) errors.push(e1);

  const e2 = checkSheriffCount(assignments);
  if (e2) errors.push(e2);

  const e3 = checkNightKillColors(assignments, level.nights);
  if (e3) errors.push(e3);

  const e4 = checkSheriffConsistency(assignments, level.sheriffClaims);
  if (e4) errors.push(e4);

  const e5 = checkParity(assignments, level.days);
  if (e5) errors.push(e5);

  return errors;
}

export function compareSolution(
  assignments: PlayerAssignment[],
  solution: Record<number, Exclude<PuzzleRole, 'grey'>>,
): boolean {
  for (const assignment of assignments) {
    const expected = solution[assignment.seat];
    if (!expected) return false;
    // maybe-* and grey are not definitive — solution not complete
    if (!isDefinitive(assignment.role)) return false;
    if (assignment.role !== expected) return false;
  }
  return true;
}

export function initAssignments(level: GameLevel): PlayerAssignment[] {
  const statuses = deriveStatuses(level.nights, level.days);
  return Array.from({ length: 10 }, (_, i) => ({
    seat: i + 1,
    role: 'grey' as PuzzleRole,
    status: statuses[i + 1] ?? ('alive' as PlayerStatus),
  }));
}
